import { actorMemoryEntrySchema } from "@arti/schemas/actorMemory";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";

import { Button } from "../../../common/Button";
import { PillButton, RetroEmpty, RetroError, SectionLabel } from "../../retro";

const memoryEntrySchema = actorMemoryEntrySchema.omit({ metadata: true });
const memoryResponseSchema = z.object({ entries: z.array(memoryEntrySchema) });
export type MemoryEntry = z.infer<typeof memoryEntrySchema>;

interface MemoryTabProps {
  instanceId: string;
}

type LayerFilter = "all" | "episodic" | "semantic";

const LAYER_OPTIONS: { label: string; value: LayerFilter }[] = [
  { label: "All", value: "all" },
  { label: "Episodic", value: "episodic" },
  { label: "Semantic", value: "semantic" },
];

const getLayerAccent = (layer: LayerFilter) => {
  if (layer === "episodic") {
    return "amber";
  }
  if (layer === "semantic") {
    return "violet";
  }
  return "emerald";
};

async function fetchJson(url: string, init?: RequestInit): Promise<unknown> {
  const resp = await fetch(url, { credentials: "include", ...init });
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }
  return resp.json();
}

export const MemoryTab: FC<MemoryTabProps> = ({ instanceId }) => {
  const [entries, setEntries] = useState<MemoryEntry[]>([]);
  const [layerFilter, setLayerFilter] = useState<LayerFilter>("all");
  const [includeSuperseded, setIncludeSuperseded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [consolidating, setConsolidating] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams();
      if (layerFilter !== "all") {
        params.set("layer", layerFilter);
      }
      if (includeSuperseded) {
        params.set("includeSuperseded", "true");
      }
      const qs = params.toString();
      const { entries: rows } = memoryResponseSchema.parse(
        await fetchJson(
          `/user/instances/${instanceId}/memory${qs.length > 0 ? `?${qs}` : ""}`,
        ),
      );
      setEntries(rows);
    } catch (error) {
      setErr(error instanceof Error ? error.message : "Failed to load memory");
    } finally {
      setLoading(false);
    }
  }, [instanceId, layerFilter, includeSuperseded]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleDelete = useCallback(
    async (entryId: string) => {
      // Confirmation is coarse on purpose — memory mutations are rare and
      // Deliberate; users editing from the drawer are operator-class.
      if (
        !globalThis.confirm("Delete this memory entry? This cannot be undone.")
      ) {
        return;
      }
      try {
        await fetchJson(`/user/instances/${instanceId}/memory/${entryId}`, {
          method: "DELETE",
        });
        await refresh();
      } catch (error) {
        setErr(error instanceof Error ? error.message : "Delete failed");
      }
    },
    [instanceId, refresh],
  );

  const handleForceConsolidate = useCallback(async () => {
    setConsolidating(true);
    setErr(null);
    try {
      await fetchJson(`/user/instances/${instanceId}/consolidate`, {
        method: "POST",
      });
      // Consolidation is asynchronous — the actor will process the tick on its
      // Next wake. Give a short beat so the user sees the action register,
      // Then refresh to pick up any new semantic entry that lands promptly.
      setTimeout(() => {
        void refresh();
      }, 1500);
    } catch (error) {
      setErr(error instanceof Error ? error.message : "Consolidation failed");
    } finally {
      setConsolidating(false);
    }
  }, [instanceId, refresh]);

  const grouped = useMemo(() => {
    const semantic = entries.filter((e) => e.layer === "semantic");
    const episodic = entries.filter((e) => e.layer === "episodic");
    return { episodic, semantic };
  }, [entries]);

  // Two real reasons for empty: never-consolidated, or filtered-to-a-layer that
  // happens to have nothing in it. The directive form names the next press;
  // the filtered form points at the layer toggle that hides them.
  const emptyCopy = useMemo(() => {
    if (layerFilter !== "all") {
      return {
        label: "NO MEMORY",
        sub: `no ${layerFilter} entries — switch to All to view all layers`,
      };
    }
    return {
      label: "NO MEMORY",
      sub: "press Force consolidate to seed the first entry",
    };
  }, [layerFilter]);

  return (
    <div className="flex h-full flex-col gap-3 p-3 sm:p-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          {LAYER_OPTIONS.map((opt) => (
            <PillButton
              key={opt.value}
              active={layerFilter === opt.value}
              onClick={() => setLayerFilter(opt.value)}
              accent={getLayerAccent(opt.value)}
            >
              {opt.label}
            </PillButton>
          ))}
        </div>
        <label className="ml-auto flex items-center gap-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">
          <input
            type="checkbox"
            checked={includeSuperseded}
            onChange={(e) => setIncludeSuperseded(e.target.checked)}
            className="accent-emerald-400"
          />
          Superseded
        </label>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void refresh()}
          disabled={loading}
        >
          Refresh
        </Button>
        <Button
          size="sm"
          onClick={() => void handleForceConsolidate()}
          loading={consolidating}
          loadingText="Consolidating…"
        >
          Force consolidate
        </Button>
      </div>

      {err !== null && <RetroError message={err} />}

      {/* Layer sections */}
      <div className="flex-1 overflow-y-auto">
        {(layerFilter === "all" || layerFilter === "semantic") && (
          <MemoryLayerSection
            title="Semantic"
            entries={grouped.semantic}
            onDelete={(id) => void handleDelete(id)}
          />
        )}
        {(layerFilter === "all" || layerFilter === "episodic") && (
          <MemoryLayerSection
            title="Episodic"
            entries={grouped.episodic}
            onDelete={(id) => void handleDelete(id)}
          />
        )}
        {!loading && entries.length === 0 && (
          <RetroEmpty label={emptyCopy.label} sub={emptyCopy.sub} />
        )}
      </div>
    </div>
  );
};

interface MemoryLayerSectionProps {
  title: string;
  entries: MemoryEntry[];
  onDelete: (id: string) => void;
}

const MemoryLayerSection: FC<MemoryLayerSectionProps> = ({
  title,
  entries,
  onDelete,
}) => {
  if (entries.length === 0) {
    return null;
  }
  const accent: "amber" | "violet" =
    title.toLowerCase() === "episodic" ? "amber" : "violet";
  return (
    <section className="mb-4">
      <SectionLabel
        label={title}
        accent={accent}
        meta={<span className="tabular-nums">{entries.length} entries</span>}
        className="mb-2"
      />
      <ul className="space-y-2">
        {entries.map((entry) => (
          <MemoryEntryRow
            key={entry.id}
            entry={entry}
            onDelete={onDelete}
            accent={accent}
          />
        ))}
      </ul>
    </section>
  );
};

interface MemoryEntryRowProps {
  entry: MemoryEntry;
  onDelete: (id: string) => void;
  accent: "amber" | "violet";
}

const MemoryEntryRow: FC<MemoryEntryRowProps> = ({
  entry,
  onDelete,
  accent,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isSuperseded = entry.supersededById !== null;
  const accentBorder =
    accent === "amber"
      ? "border-amber-500/40 dark:border-amber-400/20"
      : "border-violet-500/40 dark:border-violet-400/20";
  const accentLeft =
    accent === "amber" ? "bg-amber-400/60" : "bg-violet-400/60";
  return (
    <li
      className={`relative border px-3 py-2 ${
        isSuperseded
          ? "border-slate-300/60 bg-slate-100/40 opacity-60 dark:border-slate-700/40 dark:bg-slate-900/20"
          : `${accentBorder} bg-slate-100/60 dark:bg-slate-900/40`
      }`}
    >
      {/* Left accent edge (hidden for superseded) */}
      {!isSuperseded && (
        <span
          aria-hidden
          className={`absolute inset-y-0 left-0 w-0.5 ${accentLeft}`}
        />
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">
            <span
              className={
                accent === "amber"
                  ? "text-amber-700/90 dark:text-amber-300/80"
                  : "text-violet-700/90 dark:text-violet-300/80"
              }
            >
              [{entry.kind}]
            </span>
            <span>·</span>
            <span className="tabular-nums">
              {new Date(entry.createdAt)
                .toISOString()
                .slice(0, 16)
                .replace("T", " ")}
            </span>
            <span>·</span>
            <span className="tabular-nums">touches {entry.accessCount}</span>
            {isSuperseded && (
              <>
                <span>·</span>
                <span className="text-amber-700 dark:text-amber-300">
                  SUPERSEDED
                </span>
              </>
            )}
          </div>
          <p className="mt-1 font-mono text-[12px] leading-relaxed text-slate-900 dark:text-slate-100">
            {entry.summary}
          </p>
          {expanded && (
            <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap border border-slate-300 bg-slate-50/60 p-2 font-mono text-[10.5px] leading-relaxed text-slate-700 dark:border-slate-700/60 dark:bg-slate-950/60 dark:text-slate-300">
              {entry.body}
            </pre>
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-1">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="border border-slate-300 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-slate-600 hover:border-emerald-400/60 hover:text-emerald-700 dark:border-slate-700/60 dark:text-slate-400 dark:hover:text-emerald-300"
          >
            {expanded ? "Hide" : "Body"}
          </button>
          {!isSuperseded && (
            <button
              type="button"
              onClick={() => onDelete(entry.id)}
              className="border border-rose-500/30 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-rose-300/80 hover:border-rose-500/60 hover:text-rose-300"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </li>
  );
};
